// AI-CONTEXT: See docs/features/postcode-huisnummer-controle.md
// Klopt een ingevuld adres met wat het adressenregister (PDOK Locatieserver)
// teruggeeft? PDOK zoekt 'fuzzy': op '7201AB 40' kan het 7201AB 42 of 7201AC 40
// teruggeven, en wie het eerste antwoord blind overneemt slaat een buurman op.
// Deze helper legt het antwoord naast de invoer en zegt wat het is.

/** Nederlandse postcode: vier cijfers, twee letters, spatie optioneel. */
const isDutchPostcode = (value) => /^\d{4}\s?[A-Za-z]{2}$/.test(String(value || '').trim())

const normalizePostcode = (value) => String(value || '').toUpperCase().replace(/\s+/g, '')

/** Toevoeging zonder spaties, streepjes en hoofdlettergevoeligheid: 'a', ' A', '-a' → 'A'. */
const normalizeToevoeging = (value) => String(value || '').toUpperCase().replace(/[\s-]+/g, '')

/**
 * '12a' → nummer 12 en toevoeging 'A'. Een apart ingevulde toevoeging wint.
 * Spiegel van splitHuisnummer in functions/addressMatching/lookupBagServerSide.js;
 * de pariteit staat in lookupBagServerSide.test.js.
 * @param {*} huisnummer
 * @param {*} [toevoeging]
 * @returns {{nummer: number|null, toevoeging: string}}
 */
const splitHuisnummer = (huisnummer, toevoeging) => {
    const m = String(huisnummer || '').trim().match(/^(\d+)(.*)$/)
    if (!m) return { nummer: null, toevoeging: '' }
    const los = normalizeToevoeging(toevoeging)
    return { nummer: Number(m[1]), toevoeging: los || normalizeToevoeging(m[2]) }
}

/** Letter en toevoeging van een PDOK-adres samen, genormaliseerd om te vergelijken. */
const toevoegingOf = (doc) => normalizeToevoeging(`${doc.huisletter || ''}${doc.huisnummertoevoeging || ''}`)

/**
 * De woning zoals hij op de voordeur staat: letter direct na het nummer, een
 * toevoeging met een streepje. Niet genormaliseerd, want '12-1' zonder streepje
 * leest als huisnummer 121.
 * @param {object} doc - Een PDOK-adres
 * @returns {string}
 */
const unitLabel = (doc) => {
    const toevoeging = String(doc.huisnummertoevoeging || '').trim()
    return `${doc.huisnummer}${String(doc.huisletter || '').trim()}${toevoeging ? `-${toevoeging}` : ''}`
}

/**
 * @typedef {'match'|'mismatch'|'not-found'|'ambiguous'|'incomplete'} AddressVerdict
 */

/**
 * @typedef {object} AddressCheck
 * @property {AddressVerdict} verdict
 * @property {object|null} found - Het kloppende adres, of bij een mismatch het eerste gevonden adres
 * @property {string[]} [options] - Bij 'ambiguous', en bij 'not-found' door een onbekende toevoeging: de toevoegingen in dit pand, genormaliseerd ('' = zonder). Gelijk aan wat de server teruggeeft.
 * @property {string[]} [units] - Dezelfde woningen om te tonen ('12A', '12-2'), in de volgorde van `options`
 * @property {object} [building] - Bij `options`: een adres uit dit pand. Het gebouw is bekend, de woning niet.
 */

/**
 * Zelfde regels als de server (lookupBagServerSide): in één pand met 12A en 12B
 * zijn dat twee woningen met elk een eigen BAG-id, en wie de eerste pakt geeft
 * het huishouden het BAG-id van de buren.
 *
 * @param {Array<object>|undefined} docs - `response.docs` van PDOK (`type:adres`)
 * @param {{postcode?: string, huisnummer?: string, toevoeging?: string}} input - Wat de gebruiker invulde; het huisnummer mag de toevoeging bevatten ('12a')
 * @returns {AddressCheck}
 */
const verifyAddressMatch = (docs, { postcode, huisnummer, toevoeging } = {}) => {
    const pc = normalizePostcode(postcode)
    const { nummer, toevoeging: gezocht } = splitHuisnummer(huisnummer, toevoeging)
    if (!isDutchPostcode(postcode) || nummer === null) return { verdict: 'incomplete', found: null }

    const lijst = Array.isArray(docs) ? docs.filter(Boolean) : []
    if (lijst.length === 0) return { verdict: 'not-found', found: null }

    // PDOK zoekt fuzzy: alleen exact deze postcode en dit nummer hoort bij het pand.
    const inPand = lijst.filter((d) => normalizePostcode(d.postcode) === pc && Number(d.huisnummer) === nummer)
    if (inPand.length === 0) return { verdict: 'mismatch', found: lijst[0] }

    const options = [...new Set(inPand.map(toevoegingOf))].sort()
    const pand = {
        options,
        units: options.map((t) => unitLabel(inPand.find((d) => toevoegingOf(d) === t))),
        building: inPand[0],
    }

    if (gezocht) {
        const klopt = inPand.find((d) => toevoegingOf(d) === gezocht)
        return klopt ? { verdict: 'match', found: klopt } : { verdict: 'not-found', found: null, ...pand }
    }

    const zonder = inPand.filter((d) => toevoegingOf(d) === '')
    if (zonder.length === 1) return { verdict: 'match', found: zonder[0] }
    if (inPand.length === 1) return { verdict: 'match', found: inPand[0] }
    return { verdict: 'ambiguous', found: null, ...pand }
}

/**
 * Wat de gebruiker leest. Leeg als er niets te melden is.
 * @param {AddressCheck} uitkomst
 * @returns {string}
 */
const addressMismatchMessage = ({ verdict, found, units }) => {
    if (verdict === 'ambiguous') {
        return `Op dit adres staan meerdere woningen: ${(units || []).join(', ')}. Vul de toevoeging in.`
    }
    if (verdict === 'mismatch') {
        const adres = found
            ? `${found.straatnaam || ''} ${found.huisnummer ?? ''}${found.huisletter || ''}, ${found.postcode || ''} ${found.woonplaatsnaam || ''}`.replace(/\s+/g, ' ').trim()
            : ''
        return `Postcode en huisnummer horen niet bij elkaar. Bij deze zoekopdracht vond het adressenregister: ${adres}. Controleer de postcode en het huisnummer.`
    }
    if (verdict === 'not-found' && Array.isArray(units) && units.length > 0) {
        return `Deze toevoeging bestaat niet op dit adres. Wel gevonden: ${units.join(', ')}. Controleer de toevoeging.`
    }
    if (verdict === 'not-found') {
        return 'Dit adres is niet gevonden in het adressenregister. Controleer de postcode en het huisnummer.'
    }
    return ''
}

export { isDutchPostcode, normalizePostcode, splitHuisnummer, addressMismatchMessage }
export default verifyAddressMatch
